import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight, Star, Minus, Plus, Truck, ShieldCheck, RotateCcw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useCart } from "@/context/CartContext";
import api, { formatMoney } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const { addItem } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    setProduct(null);
    setActiveImage(0);
    api.get(`/products/${slug}`).then((res) => {
      setProduct(res.data);
      api.get("/products", { params: { category: res.data.category, limit: 4 } })
        .then((r) => setRelated((r.data.items || []).filter((p) => p.id !== res.data.id).slice(0, 4)));
    }).catch(() => setProduct(false));
  }, [slug]);

  if (product === false) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Product not found</h1>
        <Button asChild className="mt-6"><Link to="/shop">Continue Shopping</Link></Button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 grid md:grid-cols-2 gap-8">
        <Skeleton className="aspect-square w-full" />
        <div className="space-y-4">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  const discount = product.compare_at_price && product.compare_at_price > product.price
    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
    : 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <nav className="flex items-center text-xs text-slate-500 mb-4">
        <Link to="/" className="hover:text-[#0b3d75]">Home</Link>
        <ChevronRight className="w-3 h-3 mx-1" />
        <Link to={`/shop/${product.category}`} className="hover:text-[#0b3d75] capitalize">{product.category.replace(/-/g, " ")}</Link>
        <ChevronRight className="w-3 h-3 mx-1" />
        <span className="text-slate-900 line-clamp-1">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        <div>
          <div className="relative aspect-square bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
            <img src={product.images[activeImage] || product.images[0]} alt={product.name} className="w-full h-full object-contain p-6" />
            {discount > 0 && (
              <Badge className="absolute top-4 left-4 bg-amber-500 hover:bg-amber-500 text-white font-bold text-sm px-3 py-1">Save {discount}%</Badge>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`aspect-square rounded-md overflow-hidden border-2 bg-slate-50 ${activeImage === i ? "border-[#0b3d75]" : "border-slate-200 hover:border-slate-400"}`}
                >
                  <img src={img} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{product.brand}</div>
          <h1 className="mt-1 text-2xl md:text-3xl font-black text-slate-900 leading-tight">{product.name}</h1>

          <div className="mt-3 flex items-center gap-3">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`w-4 h-4 ${i <= Math.round(product.rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
              ))}
            </div>
            <div className="text-sm text-slate-600">{product.rating} ({product.review_count} reviews)</div>
            <div className="text-xs text-slate-500">SKU: {product.sku}</div>
          </div>

          <div className="mt-5 flex items-end gap-3">
            <div className="text-4xl font-black text-[#0b3d75]">{formatMoney(product.price)}</div>
            {product.compare_at_price && product.compare_at_price > product.price && (
              <div className="text-lg text-slate-400 line-through mb-1">{formatMoney(product.compare_at_price)}</div>
            )}
          </div>
          <div className="text-sm text-slate-500">per {product.unit || "each"} • <span className="text-emerald-700 font-semibold inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> In Stock ({product.stock} available)</span></div>

          <p className="mt-5 text-slate-700 leading-relaxed">{product.short_description || product.description}</p>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-slate-300 rounded-md">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-11 w-11 flex items-center justify-center hover:bg-slate-100">
                <Minus className="w-4 h-4" />
              </button>
              <div className="w-12 text-center font-bold">{qty}</div>
              <button onClick={() => setQty((q) => q + 1)} className="h-11 w-11 flex items-center justify-center hover:bg-slate-100">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <Button size="lg" onClick={() => addItem(product, qty)} className="flex-1 bg-[#0b3d75] hover:bg-[#0a4e94] h-11 font-bold">
              Add to Cart
            </Button>
            <Button size="lg" onClick={() => { addItem(product, qty); navigate("/checkout"); }} className="bg-amber-500 hover:bg-amber-400 text-slate-900 h-11 font-bold px-6">
              Buy Now
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2 text-xs">
            {[
              { icon: Truck, t: "Free ship $75+" },
              { icon: ShieldCheck, t: "Secure Square Pay" },
              { icon: RotateCcw, t: "Easy Returns" },
            ].map(({ icon: Icon, t }) => (
              <div key={t} className="flex items-center gap-2 p-3 rounded-md border border-slate-200 bg-slate-50">
                <Icon className="w-4 h-4 text-[#0b3d75]" />
                <span className="font-medium text-slate-700">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-12">
        <Tabs defaultValue="description">
          <TabsList>
            <TabsTrigger value="description">Description</TabsTrigger>
            <TabsTrigger value="specs">Specifications</TabsTrigger>
            <TabsTrigger value="shipping">Shipping & Returns</TabsTrigger>
          </TabsList>
          <TabsContent value="description" className="prose max-w-none text-slate-700 leading-relaxed mt-4">
            <p>{product.description}</p>
          </TabsContent>
          <TabsContent value="specs" className="mt-4">
            <table className="w-full text-sm max-w-xl">
              <tbody className="divide-y divide-slate-200">
                <tr><td className="py-2 pr-4 text-slate-500 font-medium w-40">SKU</td><td className="py-2 font-semibold">{product.sku}</td></tr>
                <tr><td className="py-2 pr-4 text-slate-500 font-medium">Brand</td><td className="py-2 font-semibold">{product.brand}</td></tr>
                <tr><td className="py-2 pr-4 text-slate-500 font-medium">Category</td><td className="py-2 font-semibold capitalize">{product.category.replace(/-/g, " ")}</td></tr>
                <tr><td className="py-2 pr-4 text-slate-500 font-medium">Unit</td><td className="py-2 font-semibold">{product.unit}</td></tr>
                <tr><td className="py-2 pr-4 text-slate-500 font-medium">In Stock</td><td className="py-2 font-semibold">{product.stock}</td></tr>
              </tbody>
            </table>
          </TabsContent>
          <TabsContent value="shipping" className="prose max-w-none text-slate-700 leading-relaxed mt-4">
            <p><b>Fast, insured shipping</b> from our Troy, MI warehouse and partner distribution centers. Orders over $75 ship free. Standard delivery in 2–5 business days. Returns accepted within 30 days for unopened items — see our full return policy for details.</p>
          </TabsContent>
        </Tabs>
      </div>

      {related.length > 0 && (
        <section className="mt-14">
          <h2 className="text-2xl font-black text-slate-900 mb-6">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
